import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn, formatRelative, usePickLocalized } from '@/shared/lib'

import type { Notification } from '../model/types'

type NotificationItemProps = {
  notification: Notification
  /** Called on the click that also expands the row, and only while unread. */
  onRead?: () => void
}

/**
 * The row is one button and nothing inside it is interactive: the popover is
 * the only place notifications are read now, so a click both expands the body
 * and marks the row read. A nested "read more" button would be invalid markup
 * and a second tab stop for the same action.
 */
export function NotificationItem({ notification, onRead }: NotificationItemProps) {
  const { t } = useTranslation('notifications')
  const pick = usePickLocalized()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const bodyRef = useRef<HTMLSpanElement>(null)

  const isUnread = notification.readAt === null
  const title = pick(notification.titleRu, notification.titleEn)
  const body = pick(notification.bodyRu, notification.bodyEn)

  // Measured, not guessed from the character count: a body that fits inside the
  // clamp needs no "show more", and a hint that expands nothing is a lie. Only
  // the collapsed state can be measured — expanded, the clamp is gone and the
  // element always reports itself as fitting.
  useLayoutEffect(() => {
    const element = bodyRef.current
    if (element === null || isExpanded) return
    setIsTruncated(element.scrollHeight > element.clientHeight + 1)
  }, [body, isExpanded])

  const handleClick = () => {
    setIsExpanded((expanded) => !expanded)
    if (isUnread) onRead?.()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-expanded={isExpanded}
      className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-surface-2"
    >
      <span
        aria-hidden
        className={cn(
          'mt-1.5 size-1.75 shrink-0 rounded-full',
          isUnread ? 'bg-primary' : 'bg-transparent',
        )}
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className={cn('truncate text-body', isUnread ? 'text-foreground' : 'text-fg-body')}>
            {title}
          </span>
          <span className="shrink-0 font-mono text-caption text-muted-foreground">
            {formatRelative(notification.createdAt)}
          </span>
        </span>
        {body !== '' && (
          <>
            <span
              ref={bodyRef}
              className={cn(
                // A body can be one unbroken string — pasted ids, a URL. Without
                // this it widens the popover and scrolls the titles out of view.
                'mt-1 block wrap-anywhere text-caption text-muted-foreground',
                !isExpanded && 'line-clamp-2',
              )}
            >
              {body}
            </span>
            {isTruncated && (
              <span className="mt-1 block text-caption text-primary">
                {isExpanded ? t('collapse') : t('readMore')}
              </span>
            )}
          </>
        )}
      </span>
    </button>
  )
}
